import type { BiomeBand, BiomeId } from './biome.js';
import { deriveSeed, mulberry32 } from './rand.js';

/**
 * The weather.
 *
 * The last thing M4 asked for, and the one where the temptation is to build a simulation. What it is instead:
 * four kinds of day, chosen from the route's own seed, expressed as five numbers the renderer can use. Rain,
 * wind and fog - as the to-do list has said since M0 - plus snow where snow belongs.
 *
 * Three decisions shape all of it, and the first two are refusals.
 *
 * **Weather does not touch the physics.** The plan's own list had `frictionMul` in it, and the plan was
 * wrong: she is three, the game has no fail state by design, and a car that slides differently because of
 * a cosmetic roll of the dice is a game that got harder for a reason she cannot see or control. Rain is
 * something to look at and listen to. The `assistLevel` ladder is the only thing allowed to change how the
 * car drives, because that is a decision a parent makes on purpose.
 *
 * **It is never frightening and never hides anything.** No thunder, no lightning flash - a bright strobe and
 * a bang are the two things most likely to end the evening - and the veil is capped low enough that her own
 * car reads through it at all times. A three-year-old cannot reason "it is only weather".
 *
 * **Most days are fine.** Clear is weighted at more than half, because weather is a treat when it is
 * occasional and wallpaper when it is constant. The two legs of a drive can differ by exactly one step -
 * see `wetterFor` - which is the "one gentle thing changes on the way home" the design has always asked for.
 *
 * Pure, like the rest of core: a seed and some bands in, a name out.
 */

export const WEATHER_IDS = ['clear', 'cloudy', 'rain', 'fog', 'snowfall'] as const;
export type WeatherId = (typeof WEATHER_IDS)[number];

export type Precipitation = 'none' | 'rain' | 'snow';

export interface WeatherDef {
  readonly id: WeatherId;
  readonly precip: Precipitation;
  /** How much of it falls, 0 to 1. Scaled again by the device tier before anything is drawn. */
  readonly intensity: number;
  /** Metres a second, sideways. Slants the rain, hurries the clouds, and drives the wind bed. */
  readonly windMps: number;
  /** A flat veil over the world. Capped low: her car must read through it. */
  readonly veilAlpha: number;
  readonly veilTint: number;
  /** How wet her car looks - an additive sheen over her own paint, never instead of it. */
  readonly sheen: number;
  /** How loud the rain hiss is, 0 to 1. Zero means the bed is never built. */
  readonly hiss: number;
}

export const WEATHERS: Readonly<Record<WeatherId, WeatherDef>> = {
  clear: {
    id: 'clear',
    precip: 'none',
    intensity: 0,
    windMps: 0.6,
    veilAlpha: 0,
    veilTint: 0xffffff,
    sheen: 0,
    hiss: 0,
  },
  /*
   * A grey day with a breeze in it. Nothing falls, so this is the cheapest weather there is - and it is the
   * one the return leg usually escalates *to*, which is the point: something changed, and nothing got harder.
   */
  cloudy: {
    id: 'cloudy',
    precip: 'none',
    intensity: 0,
    windMps: 2.4,
    veilAlpha: 0.08,
    veilTint: 0xb8c6d2,
    sheen: 0,
    hiss: 0,
  },
  rain: {
    id: 'rain',
    precip: 'rain',
    // Well short of a downpour. Past about 0.7 the drops start to read as static over the picture.
    intensity: 0.62,
    windMps: 3.4,
    veilAlpha: 0.14,
    veilTint: 0x9fb4c4,
    // The best half of rain: her car goes shiny. It is her drawing, lit differently.
    sheen: 0.85,
    hiss: 0.7,
  },
  fog: {
    id: 'fog',
    precip: 'none',
    intensity: 0,
    windMps: 0.4,
    /*
     * The thickest veil in the game, and still under half.
     *
     * Fog is the one weather that could genuinely hide something she needs to see - the next item on the
     * road, the ground in front of the wheels - so it goes no further than a haze that softens the distance.
     * The parallax layers are what actually sell it, because they are already pale.
     */
    veilAlpha: 0.42,
    veilTint: 0xdfe8ee,
    sheen: 0.2,
    hiss: 0,
  },
  snowfall: {
    id: 'snowfall',
    precip: 'snow',
    // More flakes than raindrops, and they fall far more slowly, so the same number reads as heavier.
    intensity: 0.55,
    windMps: 1.6,
    veilAlpha: 0.16,
    veilTint: 0xdfe8f2,
    sheen: 0.3,
    hiss: 0.25,
  },
};

/**
 * How often each kind turns up, before the biome gets a say.
 *
 * Clear is more than half of all drives on purpose (see the module comment). The others are what makes the
 * occasional one an event: "it is raining today" is only interesting if most days it is not.
 */
const WEIGHTS: Readonly<Record<WeatherId, number>> = {
  clear: 56,
  cloudy: 21,
  rain: 14,
  fog: 9,
  // Never rolled directly - it is what rain becomes where the ground is white. See `suitFor`.
  snowfall: 0,
};

export interface WeatherChoice {
  readonly seed: number;
  readonly bands: readonly BiomeBand[];
  readonly blendM?: number;
}

/**
 * What the weather is on this route.
 *
 * Its own seeded stream (`deriveSeed(seed, 'weather')`), so adding weather to a route did not move a single
 * hill, ramp or item on any route that already existed - the same reason the scatter, the props and the spray
 * each have their own.
 */
export function weatherFor(options: WeatherChoice): WeatherId {
  const rand = mulberry32(deriveSeed(options.seed, 'weather'));
  return suitFor(pick(rand()), dominantBiome(options.bands));
}

/**
 * The return leg, one step wetter - sometimes.
 *
 * `docs/DESIGN.md` has asked since M0 for "exactly one gentle thing to change on the way back", so the drive
 * home is not the drive out replayed. This is that, and it is deliberately the mildest version: one step on a
 * ladder that only ever goes clear -> cloudy -> rain, about a third of the time, decided by the same seed the
 * world came from. Nothing gets harder, nothing gets darker, and fog never arrives out of nowhere.
 *
 * The outbound leg is never touched, so the two legs can differ - which is the whole idea - while both remain
 * a pure function of one number.
 */
export function wetterFor(options: WeatherChoice & { readonly base: WeatherId }): WeatherId {
  const rand = mulberry32(deriveSeed(options.seed, 'weather-home'));
  if (rand() >= 0.34) return options.base;
  return suitFor(wetter(options.base), dominantBiome(options.bands));
}

/** One step up the ladder. Fog and snow stay as they are: neither has a gentler neighbour above it. */
export function wetter(id: WeatherId): WeatherId {
  switch (id) {
    case 'clear':
      return 'cloudy';
    case 'cloudy':
      return 'rain';
    default:
      return id;
  }
}

/**
 * Weather that suits the place.
 *
 * Two rules, both of them things a four-year-old would notice before an adult did: it snows where the ground
 * is white, and it does not rain in the desert. Everything else is left alone - fog on a beach and fog in a
 * forest are both perfectly ordinary.
 */
function suitFor(id: WeatherId, biome: BiomeId): WeatherId {
  if (id === 'rain' && biome === 'snow') return 'snowfall';
  if (id === 'rain' && biome === 'canyon') return 'cloudy';
  return id;
}

/**
 * Which biome the route is mostly in, by how many metres it covers.
 *
 * The *dominant* one rather than the one at the car, because weather is a property of the day rather than of
 * the spot she is standing on - and a route that dips through six metres of snow should not start snowing.
 */
export function dominantBiome(bands: readonly BiomeBand[]): BiomeId {
  const metres = new Map<BiomeId, number>();
  for (const band of bands) {
    const span = Math.max(0, band.toM - band.fromM);
    metres.set(band.biome, (metres.get(band.biome) ?? 0) + span);
  }

  let best: BiomeId = 'meadow';
  let most = -1;
  for (const [biome, span] of metres) {
    if (span > most) {
      most = span;
      best = biome;
    }
  }
  return best;
}

/** Roll against the weights. */
function pick(roll: number): WeatherId {
  let total = 0;
  for (const id of WEATHER_IDS) total += WEIGHTS[id];
  let cursor = Math.max(0, Math.min(0.999999, roll)) * total;
  for (const id of WEATHER_IDS) {
    cursor -= WEIGHTS[id];
    if (cursor < 0) return id;
  }
  return 'clear';
}

/*
 * There is deliberately no `precipitationAt(x)`.
 *
 * Rain that turns to snow as she crosses into the mountains is a lovely idea and it is one decision per
 * *pool*, not one per drop: the particle container holds a single texture, so switching would mean rebuilding
 * the layer mid-drive - a stutter in exchange for a detail she is not looking for. The dominant biome decides
 * once per leg, in `suitFor`, and a route that is mostly snow snows all the way along it.
 */
